import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormApiService } from '@app/core/services/form-api.service';
import {
  MeterDto,
  MeteringPointDto,
  MeteringPointService,
  MeterQueryResultDto,
  MeterService,
  Quadrant,
  UserResponse
} from '@app/api/dmc';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { InputDateDirective } from '@shared/components/input/date/input-date.directive';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { ToggleComponent } from '@shared/components/input/toggle/toggle.component';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';
import { AlertComponent } from '@shared/components/alert/alert.component';

@Component({
  selector: 'app-htx-meter-modal',
  imports: [
    ReactiveFormsModule,
    InputGroupComponent,
    TranslatePipe,
    InputDirective,
    InputDateDirective,
    LabelDirective,
    SelectDirective,
    ToggleComponent,
    AlertComponent
  ],
  templateUrl: './htx-meter-modal.component.html',
  styleUrl: './htx-meter-modal.component.css'
})
export class HtxMeterModalComponent implements OnInit, HasUnsavedChanges {
  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public form: FormGroup = new FormGroup({
    meteringPointIdentification: new FormControl<string | undefined>(undefined, Validators.required),
    meterNumber: new FormControl<string | null>(null, Validators.required),
    serialNumber: new FormControl<string | null>(null, Validators.required),
    quadrant: new FormControl<string | null>({value: null, disabled: true}),
    validFrom: new FormControl<string | null>(null, [Validators.required]),
    validTo: new FormControl<string | null>(null),
    sendMeterReadings: new FormControl<boolean | null>(false),
  });

  @Input() selectedMeter?: MeterQueryResultDto | null;

  protected readonly UserResponse = UserResponse;

  /** Meter List **/
  public meterList: MeterDto[] = [];

  /** ID **/
  public id?: string;

  /** Quadrant **/
  public quadrantList: KeyValue[] = enumToKeyValue(Quadrant, {
    omitKeys: ['NotSpecified'],
    prefixValue: 'enum.quadrant.'
  })

  /** Meteringpoint **/
  public meteringPoint?: MeteringPointDto;

  constructor(
    private readonly formApiService: FormApiService,
    private readonly meteringPointService: MeteringPointService,
    private readonly meterService: MeterService,
    private readonly translateService: TranslateService,
  ) {}

  public ngOnInit(): void {
    this.fetchMeterList();

    if (this.meteringPoint) {
      this.form.get('meteringPointIdentification')?.setValue(this.meteringPoint.identification);
    }

    if (this.selectedMeter) {
      this.applySelectedMeter(this.selectedMeter);
    } else {
      this.form.reset({
        meteringPointIdentification: this.meteringPoint?.identification,
        meterNumber: null,
        serialNumber: null,
        quadrant: null,
        validFrom: null,
        validTo: null,
        sendMeterReadings: null
      });
    }

    this.form.get('meterNumber')?.valueChanges.subscribe((value: string) => {
      const meter = this.meterList.find((m: MeterDto) => m.id === value);
      this.form.get('serialNumber')?.setValue(meter?.serialNumber || null);
    });
  }

  public fetchMeterList(): void {
    if (!this.meteringPoint?.identification) return;

    this.meterService
      .meterGetbymeteringpointGet(this.meteringPoint.identification)
      .subscribe((result: MeterDto[]) => {

        this.meterList = result;

        if (this.selectedMeter) {
          const exists = this.meterList.some(m => m.id === this.selectedMeter?.meterId);
          if (!exists) {
            this.meterList.push({
              id: this.selectedMeter.meterId,
              meterNumber: this.selectedMeter.meterNumber,
              serialNumber: this.selectedMeter.meterSerialNumber,
              alternateMeterNumber: this.selectedMeter.meterAlternateMeterNumber
            });
          }

          this.applySelectedMeter(this.selectedMeter);
        }
      })
  }

  public submit(): void {
    /** Set ID **/
    this.form.get('meteringPointIdentification')?.setValue(this.meteringPoint?.identification);

    this.formApiService.submit(
      this.form,
      (data) => this.meteringPointService.meteringPointAssociateMeterUpdateHtxPost(data),
      {
        successMessage: 'meteringpoint.meter.htx.success',
        closeModal: true
      }
    )
  }

  private applySelectedMeter(selected: MeterQueryResultDto) {
    this.form.patchValue({
      meterNumber: selected.meterNumber,
      serialNumber: selected.meterSerialNumber,
      validFrom: selected.validFrom,
      validTo: selected.validTo,
      quadrant: selected.quadrant,
      sendMeterReadings: selected.verificationMeter
    })
  }

  public getQuadrantDisplayValue(): string {
    const quadrantValue = this.form.get('quadrant')?.value;
    const match = this.quadrantList.find(q => q.key === quadrantValue);
    return match ? this.translateService.instant(match.value) : '';
  }
}
